/**
 * 🔴 useOrphanWells Hook
 * 
 * Task #79: The silent guardian that never sleeps
 * 
 * A watchdog system ensuring no active well goes unchecked.
 * Detects wells without pumper assignment, calculates production
 * impact, triggers manager alerts, and powers dashboard warnings.
 * 
 * Key Features:
 * - Real-time orphan detection with severity levels
 * - Days unassigned tracking with escalation
 * - Production impact estimates (lost revenue calculations)
 * - Dashboard banner integration
 * - Manager alert triggers
 * - Bulk assignment flow support
 * - Sorting by priority (oldest first, production value, severity)
 * - Smart caching with assignment-change refresh
 * - Offline support with cached orphan list
 * 
 * Severity Levels:
 * - 🟡 NEW: 0-1 days unassigned
 * - 🟠 WARNING: 2-3 days unassigned
 * - 🔴 CRITICAL: 4-7 days unassigned
 * - 💀 EMERGENCY: 7+ days unassigned (immediate action required)
 * 
 * "No well left behind. No check ever missed."
 * 
 * @module useOrphanWells
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCompanyStorageKey } from '../utils/tenantContext';

// Service imports
import {
  getOrphanWells,
  type WellForAssignment,
} from '../utils/services/wellAssignmentService';

import {
  notifyOrphanWellsDetected,
} from '../utils/notifications/notificationService';

import { isOrphanWell } from '../types/wellAssignment';

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Severity level for orphan wells based on days unassigned
 */
export type OrphanSeverity =
  | 'new'        // 0-1 days - just became orphaned
  | 'warning'    // 2-3 days - needs attention soon
  | 'critical'   // 4-7 days - urgent action required
  | 'emergency'; // 7+ days - production at risk, immediate action

/**
 * Sort options for orphan wells list
 */
export type OrphanSortBy =
  | 'days_unassigned'  // Oldest first (default - highest priority)
  | 'severity'         // Emergency → Critical → Warning → New
  | 'production_value' // Highest estimated production first
  | 'name'             // Alphabetical
  | 'created_at'       // When well was created (newest orphans)
  | 'last_production'; // Most recently productive wells first

/**
 * Extended orphan well info with severity and impact data
 */
export interface OrphanWellInfo {
  // Core well data
  id: string;
  name: string;
  companyId: string;
  status: string;
  
  // Legacy/assignment fields (all empty for orphans)
  assignedPumperIds: string[];
  assignedPumperId?: string;
  primaryPumperId?: string;
  primaryPumperName?: string;
  backupPumperId?: string;
  backupPumperName?: string;
  
  // Orphan-specific data
  severity: OrphanSeverity;
  severityScore: number;         // 1-4 (1=new, 4=emergency)
  daysUnassigned: number;        // Days since last assignment
  becameOrphanAt: Date | null;   // When assignment was removed
  
  // Production impact estimates
  estimatedDailyProduction: number;  // BBL/day
  estimatedMissedProduction: number; // BBL since orphaned
  estimatedLostRevenue: number;      // $ at $70/BBL
  productionRisk: 'low' | 'medium' | 'high' | 'critical';
  
  // Additional context
  lastProductionDate?: Date;     // Last time production was logged
  lastCheckDate?: Date;          // Last time anyone checked
  daysSinceLastCheck: number;    // Days without any check
  
  // Location (if available)
  location?: {
    address?: string;
    city?: string;
    state?: string;
    coordinates?: { lat: number; lng: number };
  };
  
  // Quick action flags
  needsImmediateAttention: boolean;  // Emergency or critical
  canAutoAssign: boolean;            // Has nearby pumper
  suggestedPumperId?: string;        // Nearest pumper by workload
  suggestedPumperName?: string;
}

/**
 * Aggregated orphan statistics for dashboard cards
 */
export interface OrphanStatistics {
  // Counts by severity
  total: number;
  newCount: number;       // 0-1 days
  warningCount: number;   // 2-3 days
  criticalCount: number;  // 4-7 days
  emergencyCount: number; // 7+ days
  
  // Impact totals
  totalDaysUnassigned: number;  // Sum of all days
  avgDaysUnassigned: number;    // Average
  maxDaysUnassigned: number;    // Worst case
  
  // Production impact
  totalMissedProduction: number;    // BBL
  totalLostRevenue: number;         // $
  estimatedDailyLoss: number;       // $ per day if unchanged
  
  // Risk assessment
  overallRisk: 'none' | 'low' | 'medium' | 'high' | 'critical';
  riskTrend: 'improving' | 'stable' | 'worsening';
  
  // Timestamps
  lastUpdated: Date;
  lastAlertSent?: Date;
}

/**
 * Dashboard banner props derived from orphan data
 */
export interface OrphanBannerData {
  show: boolean;
  severity: OrphanSeverity | 'none';
  message: string;
  shortMessage: string;  // For mobile/compact view
  icon: string;          // Emoji for quick scanning
  color: string;         // Tailwind color class
  count: number;
  actionLabel: string;
  actionUrl: string;
}

/**
 * Alert trigger conditions and thresholds
 */
export interface AlertThresholds {
  newOrphanNotify: boolean;       // Notify immediately when well becomes orphan
  dailyDigest: boolean;           // Send daily summary at scheduled time
  escalateAfterDays: number;      // Days before escalating (default: 3)
  emergencyThresholdDays: number; // Days before emergency status (default: 7)
  productionThreshold: number;    // BBL/day to flag as high-impact
}

/**
 * Hook state interface
 */
interface OrphanWellsState {
  // Lists
  orphanWells: OrphanWellInfo[];
  filteredOrphans: OrphanWellInfo[];
  
  // Statistics
  statistics: OrphanStatistics;
  banner: OrphanBannerData;
  
  // UI State
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  
  // Timestamps
  lastFetch: Date | null;
  cacheExpiry: Date | null;
}

/**
 * Hook options
 */
export interface UseOrphanWellsOptions {
  // Auto-refresh settings
  autoRefresh?: boolean;
  refreshInterval?: number;    // ms, default: 5 minutes
  refreshOnFocus?: boolean;    // Refresh when window regains focus
  refreshOnAssignmentChange?: boolean; // Refresh when assignment event fires
  
  // Filtering
  statuses?: string[];         // Well statuses to check (default: ['active'])
  severityFilter?: OrphanSeverity[]; // Only show certain severities
  
  // Sorting
  sortBy?: OrphanSortBy;
  sortDirection?: 'asc' | 'desc';
  
  // Limits
  limit?: number;              // Max orphans to load
  
  // Alert config
  alertThresholds?: Partial<AlertThresholds>;
  
  // Manager IDs for alerts
  managerIds?: string[];
}

/**
 * Hook return type
 */
export interface UseOrphanWellsReturn {
  // Data
  orphanWells: OrphanWellInfo[];
  statistics: OrphanStatistics;
  banner: OrphanBannerData;
  
  // State
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  isEmpty: boolean;
  hasCritical: boolean;
  hasEmergency: boolean;
  
  // Actions
  refresh: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  
  // Sorting & Filtering
  sortBy: (sortBy: OrphanSortBy, direction?: 'asc' | 'desc') => void;
  filterBySeverity: (severities: OrphanSeverity[]) => void;
  clearFilters: () => void;
  
  // Quick operations
  getOrphanById: (wellId: string) => OrphanWellInfo | undefined;
  getOrphansByIds: (wellIds: string[]) => OrphanWellInfo[];
  getEmergencyOrphans: () => OrphanWellInfo[];
  getCriticalOrphans: () => OrphanWellInfo[];
  getTopPriorityOrphans: (count: number) => OrphanWellInfo[];
  
  // Alert management
  sendOrphanAlert: (managerIds?: string[]) => Promise<boolean>;
  shouldSendAlert: () => boolean;
  
  // Timestamps
  lastFetch: Date | null;
  isStale: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

/** Cache key for localStorage */
const CACHE_KEY = 'orphan_wells';

/** Cache TTL in milliseconds (3 minutes - orphans need fresh data) */
const CACHE_TTL = 3 * 60 * 1000;

/** Default refresh interval (5 minutes) */
const DEFAULT_REFRESH_INTERVAL = 5 * 60 * 1000;

/** Stale threshold (2 minutes) */
const STALE_THRESHOLD = 2 * 60 * 1000;

/** Default alert thresholds */
const DEFAULT_ALERT_THRESHOLDS: AlertThresholds = {
  newOrphanNotify: true,
  dailyDigest: true,
  escalateAfterDays: 3,
  emergencyThresholdDays: 7,
  productionThreshold: 10, // 10 BBL/day considered significant
};

/** Estimated oil price for revenue calculations */
const OIL_PRICE_PER_BBL = 70;

/** Severity thresholds (days) */
const SEVERITY_THRESHOLDS = {
  new: 1,
  warning: 3,
  critical: 7,
  emergency: 7, // 7+ days
};

/** UI colors for severity levels */
const SEVERITY_COLORS: Record<OrphanSeverity, string> = {
  new: 'yellow',
  warning: 'orange',
  critical: 'red',
  emergency: 'red',
};

/** UI icons for severity levels */
const SEVERITY_ICONS: Record<OrphanSeverity, string> = {
  new: '🟡',
  warning: '🟠',
  critical: '🔴',
  emergency: '💀',
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Calculate orphan severity based on days unassigned
 */
function calculateSeverity(daysUnassigned: number): OrphanSeverity {
  if (daysUnassigned >= SEVERITY_THRESHOLDS.emergency) return 'emergency';
  if (daysUnassigned >= SEVERITY_THRESHOLDS.critical) return 'critical';
  if (daysUnassigned >= SEVERITY_THRESHOLDS.warning) return 'warning';
  return 'new';
}

/**
 * Get numeric severity score (1-4) for sorting
 */
function getSeverityScore(severity: OrphanSeverity): number {
  switch (severity) {
    case 'emergency': return 4;
    case 'critical': return 3;
    case 'warning': return 2;
    case 'new': return 1;
  }
}

/**
 * Calculate production risk level
 */
function calculateProductionRisk(
  estimatedDaily: number,
  daysUnassigned: number
): 'low' | 'medium' | 'high' | 'critical' {
  const missedProduction = estimatedDaily * daysUnassigned;
  const lostRevenue = missedProduction * OIL_PRICE_PER_BBL;
  
  if (lostRevenue >= 10000 || daysUnassigned >= 7) return 'critical';
  if (lostRevenue >= 5000 || daysUnassigned >= 4) return 'high';
  if (lostRevenue >= 1000 || daysUnassigned >= 2) return 'medium';
  return 'low';
}

/**
 * Calculate overall risk from orphan statistics
 */
function calculateOverallRisk(stats: Pick<OrphanStatistics, 'total' | 'emergencyCount' | 'criticalCount' | 'warningCount'>): OrphanStatistics['overallRisk'] {
  if (stats.total === 0) return 'none';
  if (stats.emergencyCount > 0) return 'critical';
  if (stats.criticalCount > 0) return 'high';
  if (stats.warningCount > 0) return 'medium';
  return 'low';
}

/**
 * Transform raw well data into OrphanWellInfo
 */
function transformToOrphanInfo(
  well: WellForAssignment,
  _additionalData?: {
    becameOrphanAt?: Date;
    lastProductionDate?: Date;
    lastCheckDate?: Date;
    estimatedDailyProduction?: number;
    location?: OrphanWellInfo['location'];
  }
): OrphanWellInfo {
  // Calculate days unassigned (estimate from well data or default to 1)
  // In real implementation, this would come from assignment history
  const now = new Date();
  const becameOrphanAt = _additionalData?.becameOrphanAt || null;
  const daysUnassigned = becameOrphanAt 
    ? Math.floor((now.getTime() - becameOrphanAt.getTime()) / (1000 * 60 * 60 * 24))
    : 1; // Default to 1 day if unknown
  
  const severity = calculateSeverity(daysUnassigned);
  const estimatedDaily = _additionalData?.estimatedDailyProduction || 5; // Default 5 BBL/day
  const missedProduction = estimatedDaily * daysUnassigned;
  const lostRevenue = missedProduction * OIL_PRICE_PER_BBL;
  
  const lastCheckDate = _additionalData?.lastCheckDate;
  const daysSinceLastCheck = lastCheckDate
    ? Math.floor((now.getTime() - lastCheckDate.getTime()) / (1000 * 60 * 60 * 24))
    : daysUnassigned;
  
  return {
    // Core data
    id: well.id,
    name: well.name,
    companyId: well.companyId,
    status: well.status,
    
    // Assignment fields (all empty for orphans)
    assignedPumperIds: well.assignedPumperIds || [],
    assignedPumperId: well.assignedPumperId,
    primaryPumperId: well.primaryPumperId,
    primaryPumperName: well.primaryPumperName,
    backupPumperId: well.backupPumperId,
    backupPumperName: well.backupPumperName,
    
    // Orphan-specific
    severity,
    severityScore: getSeverityScore(severity),
    daysUnassigned,
    becameOrphanAt,
    
    // Production impact
    estimatedDailyProduction: estimatedDaily,
    estimatedMissedProduction: missedProduction,
    estimatedLostRevenue: lostRevenue,
    productionRisk: calculateProductionRisk(estimatedDaily, daysUnassigned),
    
    // Check history
    lastProductionDate: _additionalData?.lastProductionDate,
    lastCheckDate,
    daysSinceLastCheck,
    
    // Location
    location: _additionalData?.location,
    
    // Quick action flags
    needsImmediateAttention: severity === 'emergency' || severity === 'critical',
    canAutoAssign: false, // Would need pumper proximity data
    suggestedPumperId: undefined,
    suggestedPumperName: undefined,
  };
}

/**
 * Calculate aggregated statistics from orphan list
 */
function calculateStatistics(
  orphans: OrphanWellInfo[],
  previousStats?: OrphanStatistics
): OrphanStatistics {
  const now = new Date();
  
  if (orphans.length === 0) {
    return {
      total: 0,
      newCount: 0,
      warningCount: 0,
      criticalCount: 0,
      emergencyCount: 0,
      totalDaysUnassigned: 0,
      avgDaysUnassigned: 0,
      maxDaysUnassigned: 0,
      totalMissedProduction: 0,
      totalLostRevenue: 0,
      estimatedDailyLoss: 0,
      overallRisk: 'none',
      riskTrend: previousStats ? 'improving' : 'stable',
      lastUpdated: now,
      lastAlertSent: previousStats?.lastAlertSent,
    };
  }
  
  // Count by severity
  let newCount = 0;
  let warningCount = 0;
  let criticalCount = 0;
  let emergencyCount = 0;
  let totalDays = 0;
  let maxDays = 0;
  let totalMissed = 0;
  let totalLost = 0;
  let dailyLoss = 0;
  
  for (const orphan of orphans) {
    switch (orphan.severity) {
      case 'new': newCount++; break;
      case 'warning': warningCount++; break;
      case 'critical': criticalCount++; break;
      case 'emergency': emergencyCount++; break;
    }
    
    totalDays += orphan.daysUnassigned;
    maxDays = Math.max(maxDays, orphan.daysUnassigned);
    totalMissed += orphan.estimatedMissedProduction;
    totalLost += orphan.estimatedLostRevenue;
    dailyLoss += orphan.estimatedDailyProduction * OIL_PRICE_PER_BBL;
  }
  
  const stats: OrphanStatistics = {
    total: orphans.length,
    newCount,
    warningCount,
    criticalCount,
    emergencyCount,
    totalDaysUnassigned: totalDays,
    avgDaysUnassigned: totalDays / orphans.length,
    maxDaysUnassigned: maxDays,
    totalMissedProduction: totalMissed,
    totalLostRevenue: totalLost,
    estimatedDailyLoss: dailyLoss,
    overallRisk: calculateOverallRisk({ total: orphans.length, emergencyCount, criticalCount, warningCount }),
    riskTrend: 'stable',
    lastUpdated: now,
    lastAlertSent: previousStats?.lastAlertSent,
  };
  
  // Determine trend
  if (previousStats) {
    if (stats.total < previousStats.total) {
      stats.riskTrend = 'improving';
    } else if (stats.total > previousStats.total || stats.emergencyCount > previousStats.emergencyCount) {
      stats.riskTrend = 'worsening';
    }
  }
  
  return stats;
}

/**
 * Generate banner data from statistics
 */
function generateBannerData(stats: OrphanStatistics, orphans: OrphanWellInfo[]): OrphanBannerData {
  if (stats.total === 0) {
    return {
      show: false,
      severity: 'none',
      message: 'All wells have assigned pumpers',
      shortMessage: 'All clear',
      icon: '✅',
      color: 'green',
      count: 0,
      actionLabel: 'View Wells',
      actionUrl: '/wells',
    };
  }
  
  // Determine severity for banner (highest severity present)
  let bannerSeverity: OrphanSeverity = 'new';
  if (stats.emergencyCount > 0) bannerSeverity = 'emergency';
  else if (stats.criticalCount > 0) bannerSeverity = 'critical';
  else if (stats.warningCount > 0) bannerSeverity = 'warning';
  
  // Build message
  const parts: string[] = [];
  if (stats.emergencyCount > 0) {
    parts.push(`${stats.emergencyCount} EMERGENCY (7+ days)`);
  }
  if (stats.criticalCount > 0) {
    parts.push(`${stats.criticalCount} critical (4-7 days)`);
  }
  if (stats.warningCount > 0) {
    parts.push(`${stats.warningCount} warning (2-3 days)`);
  }
  if (stats.newCount > 0) {
    parts.push(`${stats.newCount} new`);
  }
  
  const detailMessage = parts.join(', ');
  const lostRevenueStr = stats.totalLostRevenue >= 1000 
    ? `$${(stats.totalLostRevenue / 1000).toFixed(1)}K`
    : `$${stats.totalLostRevenue.toFixed(0)}`;
  
  const fullMessage = `${stats.total} well${stats.total === 1 ? '' : 's'} without assigned pumper: ${detailMessage}. Est. ${lostRevenueStr} in missed revenue.`;
  
  const shortMessage = stats.emergencyCount > 0
    ? `🚨 ${stats.emergencyCount} EMERGENCY well${stats.emergencyCount === 1 ? '' : 's'}!`
    : `${stats.total} orphan well${stats.total === 1 ? '' : 's'}`;
  
  // Top priority wells for action
  const topPriority = orphans
    .filter(o => o.severity === bannerSeverity)
    .slice(0, 3)
    .map(o => o.name)
    .join(', ');
  
  return {
    show: true,
    severity: bannerSeverity,
    message: fullMessage + (topPriority ? ` Priority: ${topPriority}` : ''),
    shortMessage,
    icon: SEVERITY_ICONS[bannerSeverity],
    color: SEVERITY_COLORS[bannerSeverity],
    count: stats.total,
    actionLabel: stats.emergencyCount > 0 ? '🚨 Assign Now' : 'Assign Wells',
    actionUrl: '/manager/well-assignments?filter=orphan',
  };
}

/**
 * Sort orphan wells by specified criteria
 */
function sortOrphans(
  orphans: OrphanWellInfo[],
  sortBy: OrphanSortBy,
  direction: 'asc' | 'desc'
): OrphanWellInfo[] {
  const sorted = [...orphans].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'days_unassigned':
        comparison = b.daysUnassigned - a.daysUnassigned; // Oldest first by default
        break;
      case 'severity':
        comparison = b.severityScore - a.severityScore; // Highest severity first
        if (comparison === 0) {
          comparison = b.daysUnassigned - a.daysUnassigned; // Then by days
        }
        break;
      case 'production_value':
        comparison = b.estimatedLostRevenue - a.estimatedLostRevenue;
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'created_at': {
        // Newer orphans first (becameOrphanAt)
        const aDate = a.becameOrphanAt?.getTime() || 0;
        const bDate = b.becameOrphanAt?.getTime() || 0;
        comparison = bDate - aDate;
        break;
      }
      case 'last_production': {
        const aLast = a.lastProductionDate?.getTime() || 0;
        const bLast = b.lastProductionDate?.getTime() || 0;
        comparison = bLast - aLast;
        break;
      }
    }
    
    return direction === 'desc' ? comparison : -comparison;
  });
  
  return sorted;
}

/**
 * Load orphan cache from localStorage
 */
function loadFromCache(): { data: OrphanWellInfo[]; stats: OrphanStatistics; timestamp: Date } | null {
  try {
    const key = getCompanyStorageKey(CACHE_KEY);
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const parsed = JSON.parse(cached);
    const timestamp = new Date(parsed.timestamp);
    
    // Check if cache is expired
    if (Date.now() - timestamp.getTime() > CACHE_TTL) {
      return null;
    }
    
    // Restore dates
    const data = parsed.data.map((o: OrphanWellInfo) => ({
      ...o,
      becameOrphanAt: o.becameOrphanAt ? new Date(o.becameOrphanAt) : null,
      lastProductionDate: o.lastProductionDate ? new Date(o.lastProductionDate) : undefined,
      lastCheckDate: o.lastCheckDate ? new Date(o.lastCheckDate) : undefined,
    }));
    
    const stats = {
      ...parsed.stats,
      lastUpdated: new Date(parsed.stats.lastUpdated),
      lastAlertSent: parsed.stats.lastAlertSent ? new Date(parsed.stats.lastAlertSent) : undefined,
    };
    
    return { data, stats, timestamp };
  } catch (error) {
    console.warn('Failed to load orphan cache:', error);
    return null;
  }
}

/**
 * Save orphan data to cache
 */
function saveToCache(orphans: OrphanWellInfo[], stats: OrphanStatistics): void {
  try {
    const key = getCompanyStorageKey(CACHE_KEY);
    const cacheData = {
      data: orphans,
      stats,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to save orphan cache:', error);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════════════

const initialStatistics: OrphanStatistics = {
  total: 0,
  newCount: 0,
  warningCount: 0,
  criticalCount: 0,
  emergencyCount: 0,
  totalDaysUnassigned: 0,
  avgDaysUnassigned: 0,
  maxDaysUnassigned: 0,
  totalMissedProduction: 0,
  totalLostRevenue: 0,
  estimatedDailyLoss: 0,
  overallRisk: 'none',
  riskTrend: 'stable',
  lastUpdated: new Date(),
};

const initialBanner: OrphanBannerData = {
  show: false,
  severity: 'none',
  message: 'Loading orphan well data...',
  shortMessage: 'Loading...',
  icon: '⏳',
  color: 'gray',
  count: 0,
  actionLabel: 'Loading',
  actionUrl: '/wells',
};

const initialState: OrphanWellsState = {
  orphanWells: [],
  filteredOrphans: [],
  statistics: initialStatistics,
  banner: initialBanner,
  loading: true,
  refreshing: false,
  error: null,
  lastFetch: null,
  cacheExpiry: null,
};

// Default constants to prevent infinite loops in useEffect dependencies
const DEFAULT_STATUSES = ['active'];
const DEFAULT_SEVERITY_FILTER: OrphanSeverity[] = [];
const DEFAULT_ALERT_THRESHOLDS_INPUT = {};
const DEFAULT_MANAGER_IDS: string[] = [];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOOK
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Hook for managing and monitoring orphan wells (wells without pumper assignment)
 */
export function useOrphanWells(options: UseOrphanWellsOptions = {}): UseOrphanWellsReturn {
  // Options with defaults
  const {
    autoRefresh = true,
    refreshInterval = DEFAULT_REFRESH_INTERVAL,
    refreshOnFocus = true,
    refreshOnAssignmentChange = true,
    statuses = DEFAULT_STATUSES,
    severityFilter = DEFAULT_SEVERITY_FILTER,
    sortBy: initialSortBy = 'severity',
    sortDirection: initialSortDirection = 'desc',
    limit,
    alertThresholds = DEFAULT_ALERT_THRESHOLDS_INPUT,
    managerIds = DEFAULT_MANAGER_IDS,
  } = options;
  
  // Merge alert thresholds with defaults
  // Memoize to prevent infinite loops
  const thresholds = useMemo(() => ({
    ...DEFAULT_ALERT_THRESHOLDS,
    ...alertThresholds,
  }), [JSON.stringify(alertThresholds)]);

  // Memoize array options to prevent infinite loops
  const stableStatuses = useMemo(() => statuses, [statuses.join(',')]);
  const stableManagerIds = useMemo(() => managerIds, [managerIds.join(',')]);
  
  // State
  const [state, setState] = useState<OrphanWellsState>(initialState);
  const [currentSortBy, setCurrentSortBy] = useState<OrphanSortBy>(initialSortBy);
  const [currentSortDirection, setCurrentSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);
  const [currentSeverityFilter, setCurrentSeverityFilter] = useState<OrphanSeverity[]>(severityFilter);
  
  // Refs
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<Date | null>(null);
  const previousStatsRef = useRef<OrphanStatistics | null>(null);
  const isMountedRef = useRef(true);
  const alertAttemptedRef = useRef(false); // Guard: only one alert attempt per mount
  
  // ─────────────────────────────────────────────────────────────────────────────
  // FETCH ORPHAN WELLS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const fetchOrphanWells = useCallback(async (forceRefresh = false): Promise<void> => {
    // Skip if not mounted
    if (!isMountedRef.current) return;
    
    // Try cache first if not forcing refresh
    if (!forceRefresh) {
      const cached = loadFromCache();
      if (cached) {
        const sortedData = sortOrphans(cached.data, currentSortBy, currentSortDirection);
        const filteredData = currentSeverityFilter.length > 0
          ? sortedData.filter(o => currentSeverityFilter.includes(o.severity))
          : sortedData;
        
        setState(prev => ({
          ...prev,
          orphanWells: sortedData,
          filteredOrphans: filteredData,
          statistics: cached.stats,
          banner: generateBannerData(cached.stats, sortedData),
          loading: false,
          lastFetch: cached.timestamp,
          cacheExpiry: new Date(cached.timestamp.getTime() + CACHE_TTL),
        }));
        
        previousStatsRef.current = cached.stats;
        lastFetchRef.current = cached.timestamp;
        return;
      }
    }
    
    // Set loading state
    setState(prev => ({
      ...prev,
      loading: !prev.orphanWells.length,
      refreshing: prev.orphanWells.length > 0,
      error: null,
    }));
    
    try {
      // Fetch from service
      const rawOrphans = await getOrphanWells({
        statuses: stableStatuses,
        limit: limit ? limit * 2 : undefined, // Over-fetch for filtering
      });
      
      // Transform to enhanced orphan info
      const orphanWells: OrphanWellInfo[] = rawOrphans.map(well => 
        transformToOrphanInfo(well)
      );
      
      // Calculate statistics
      const statistics = calculateStatistics(orphanWells, previousStatsRef.current || undefined);
      previousStatsRef.current = statistics;
      
      // Sort
      const sortedOrphans = sortOrphans(orphanWells, currentSortBy, currentSortDirection);
      
      // Apply limit if specified
      const limitedOrphans = limit ? sortedOrphans.slice(0, limit) : sortedOrphans;
      
      // Apply severity filter
      const filteredOrphans = currentSeverityFilter.length > 0
        ? limitedOrphans.filter(o => currentSeverityFilter.includes(o.severity))
        : limitedOrphans;
      
      // Generate banner
      const banner = generateBannerData(statistics, limitedOrphans);
      
      const now = new Date();
      lastFetchRef.current = now;
      
      // Save to cache
      saveToCache(limitedOrphans, statistics);
      
      // Update state
      if (isMountedRef.current) {
        setState({
          orphanWells: limitedOrphans,
          filteredOrphans,
          statistics,
          banner,
          loading: false,
          refreshing: false,
          error: null,
          lastFetch: now,
          cacheExpiry: new Date(now.getTime() + CACHE_TTL),
        });
      }
      
      // Log significant findings (only on meaningful changes to reduce console spam)
      const prevTotal = previousStatsRef.current?.total ?? -1;
      const countsChanged = prevTotal !== statistics.total;
      
      if (countsChanged) {
        if (statistics.emergencyCount > 0) {
          console.error(`EMERGENCY: ${statistics.emergencyCount} wells orphaned 7+ days!`);
        } else if (statistics.criticalCount > 0) {
          console.warn(`CRITICAL: ${statistics.criticalCount} wells orphaned 4-7 days`);
        } else if (statistics.total > 0) {
          console.info(`Orphan wells: ${statistics.total}`);
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch orphan wells:', error);
      
      if (isMountedRef.current) {
        setState(prev => ({
          ...prev,
          loading: false,
          refreshing: false,
          error: error instanceof Error ? error.message : 'Failed to load orphan wells',
        }));
      }
    }
  }, [limit, currentSortBy, currentSortDirection, currentSeverityFilter, stableStatuses]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // REFRESH ACTIONS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const refresh = useCallback(async (): Promise<void> => {
    await fetchOrphanWells(false);
  }, [fetchOrphanWells]);
  
  const forceRefresh = useCallback(async (): Promise<void> => {
    await fetchOrphanWells(true);
  }, [fetchOrphanWells]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // SORTING & FILTERING
  // ─────────────────────────────────────────────────────────────────────────────
  
  const sortByFn = useCallback((newSortBy: OrphanSortBy, direction?: 'asc' | 'desc'): void => {
    setCurrentSortBy(newSortBy);
    if (direction) setCurrentSortDirection(direction);
    
    setState(prev => {
      const sorted = sortOrphans(prev.orphanWells, newSortBy, direction || currentSortDirection);
      const filtered = currentSeverityFilter.length > 0
        ? sorted.filter(o => currentSeverityFilter.includes(o.severity))
        : sorted;
      return {
        ...prev,
        orphanWells: sorted,
        filteredOrphans: filtered,
      };
    });
  }, [currentSortDirection, currentSeverityFilter]);
  
  const filterBySeverity = useCallback((severities: OrphanSeverity[]): void => {
    setCurrentSeverityFilter(severities);
    
    setState(prev => {
      const filtered = severities.length > 0
        ? prev.orphanWells.filter(o => severities.includes(o.severity))
        : prev.orphanWells;
      return {
        ...prev,
        filteredOrphans: filtered,
      };
    });
  }, []);
  
  const clearFilters = useCallback((): void => {
    setCurrentSeverityFilter([]);
    setState(prev => ({
      ...prev,
      filteredOrphans: prev.orphanWells,
    }));
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // QUICK OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const getOrphanById = useCallback((wellId: string): OrphanWellInfo | undefined => {
    return state.orphanWells.find(o => o.id === wellId);
  }, [state.orphanWells]);
  
  const getOrphansByIds = useCallback((wellIds: string[]): OrphanWellInfo[] => {
    const idSet = new Set(wellIds);
    return state.orphanWells.filter(o => idSet.has(o.id));
  }, [state.orphanWells]);
  
  const getEmergencyOrphans = useCallback((): OrphanWellInfo[] => {
    return state.orphanWells.filter(o => o.severity === 'emergency');
  }, [state.orphanWells]);
  
  const getCriticalOrphans = useCallback((): OrphanWellInfo[] => {
    return state.orphanWells.filter(o => o.severity === 'critical' || o.severity === 'emergency');
  }, [state.orphanWells]);
  
  const getTopPriorityOrphans = useCallback((count: number): OrphanWellInfo[] => {
    // Already sorted by severity/days, just take top N
    return state.filteredOrphans.slice(0, count);
  }, [state.filteredOrphans]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // ALERT MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────────
  
  const shouldSendAlert = useCallback((): boolean => {
    const { statistics } = state;
    
    // No orphans = no alert
    if (statistics.total === 0) return false;
    
    // Always alert on emergency
    if (statistics.emergencyCount > 0) return true;
    
    // Alert if critical and threshold days passed
    if (statistics.criticalCount > 0 && thresholds.escalateAfterDays <= 4) return true;
    
    // Check if enough time passed since last alert (avoid spam)
    if (statistics.lastAlertSent) {
      const hoursSinceLastAlert = (Date.now() - statistics.lastAlertSent.getTime()) / (1000 * 60 * 60);
      if (hoursSinceLastAlert < 4) return false; // At least 4 hours between alerts
    }
    
    return statistics.total > 0 && thresholds.newOrphanNotify;
  }, [state, thresholds]);
  
  const sendOrphanAlert = useCallback(async (targetManagerIds?: string[]): Promise<boolean> => {
    const targets = targetManagerIds || stableManagerIds;
    
    if (targets.length === 0) {
      console.warn('No manager IDs provided for orphan alert');
      return false;
    }
    
    if (state.orphanWells.length === 0) {
      console.info('No orphan wells to alert about');
      return false;
    }
    
    try {
      const wellInfos = state.orphanWells.map(o => ({
        id: o.id,
        name: o.name,
      }));
      
      await notifyOrphanWellsDetected(targets, wellInfos);
      
      // Update last alert sent time
      setState(prev => ({
        ...prev,
        statistics: {
          ...prev.statistics,
          lastAlertSent: new Date(),
        },
      }));
      
      console.info(`Orphan well alert sent to ${targets.length} manager(s)`);
      return true;
      
    } catch (error) {
      console.error('Failed to send orphan alert:', error);
      return false;
    }
  }, [state.orphanWells, stableManagerIds]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // COMPUTED VALUES
  // ─────────────────────────────────────────────────────────────────────────────
  
  const isEmpty = useMemo(() => state.orphanWells.length === 0, [state.orphanWells]);
  
  const hasCritical = useMemo(() => 
    state.statistics.criticalCount > 0 || state.statistics.emergencyCount > 0,
    [state.statistics]
  );
  
  const hasEmergency = useMemo(() => 
    state.statistics.emergencyCount > 0,
    [state.statistics]
  );
  
  const isStale = useMemo(() => {
    if (!state.lastFetch) return true;
    return Date.now() - state.lastFetch.getTime() > STALE_THRESHOLD;
  }, [state.lastFetch]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  
  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    fetchOrphanWells(false);
    
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchOrphanWells]);
  
  // Auto-refresh timer
  useEffect(() => {
    if (!autoRefresh) return;
    
    refreshTimerRef.current = setInterval(() => {
      if (isMountedRef.current && !state.loading && !state.refreshing) {
        fetchOrphanWells(true);
      }
    }, refreshInterval);
    
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchOrphanWells, state.loading, state.refreshing]);
  
  // Refresh on window focus
  useEffect(() => {
    if (!refreshOnFocus) return;
    
    const handleFocus = () => {
      if (isStale && isMountedRef.current) {
        fetchOrphanWells(true);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshOnFocus, isStale, fetchOrphanWells]);
  
  // Refresh on assignment change event
  useEffect(() => {
    if (!refreshOnAssignmentChange) return;
    
    const handleAssignmentChange = () => {
      if (isMountedRef.current) {
        // Small delay to let Firestore propagate
        setTimeout(() => fetchOrphanWells(true), 500);
      }
    };
    
    // Listen for custom event (fired by assignment operations)
    window.addEventListener('wellAssignmentChanged', handleAssignmentChange);
    return () => window.removeEventListener('wellAssignmentChanged', handleAssignmentChange);
  }, [refreshOnAssignmentChange, fetchOrphanWells]);
  
  // Auto-alert managers when orphan wells are detected (once per mount, throttled by Firestore)
  useEffect(() => {
    // Guard: only attempt once per mount/session
    if (alertAttemptedRef.current) return;
    
    // Wait for data to load
    if (state.loading) return;
    
    // No orphans = no alert needed
    if (state.orphanWells.length === 0) return;
    
    // Check if we should send (respects threshold settings AND severity)
    if (!shouldSendAlert()) return;
    
    // No managers to notify = fail gracefully (banner will still show)
    if (managerIds.length === 0) return;
    
    // Mark as attempted BEFORE async call to prevent race conditions
    alertAttemptedRef.current = true;
    
    // Fire and forget - the 24h Firestore throttle inside notifyOrphanWellsDetected() is the hard gate
    sendOrphanAlert(managerIds).then(sent => {
      if (sent) {
        console.info('Auto-alert: Orphan wells notification sent to managers');
      } else {
        console.info('Auto-alert: Skipped (throttled or no orphans)');
      }
    }).catch(err => {
      console.warn('Auto-alert failed:', err);
    });
  }, [state.loading, state.orphanWells.length, shouldSendAlert, managerIds, sendOrphanAlert]);
  
  // ─────────────────────────────────────────────────────────────────────────────
  // RETURN
  // ─────────────────────────────────────────────────────────────────────────────
  
  return {
    // Data
    orphanWells: state.filteredOrphans,
    statistics: state.statistics,
    banner: state.banner,
    
    // State
    loading: state.loading,
    refreshing: state.refreshing,
    error: state.error,
    isEmpty,
    hasCritical,
    hasEmergency,
    
    // Actions
    refresh,
    forceRefresh,
    
    // Sorting & Filtering
    sortBy: sortByFn,
    filterBySeverity,
    clearFilters,
    
    // Quick operations
    getOrphanById,
    getOrphansByIds,
    getEmergencyOrphans,
    getCriticalOrphans,
    getTopPriorityOrphans,
    
    // Alerts
    sendOrphanAlert,
    shouldSendAlert,
    
    // Timestamps
    lastFetch: state.lastFetch,
    isStale,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONVENIENCE HOOKS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Hook for the dashboard warning banner
 * Minimal data, optimized for banner display
 */
export function useOrphanWellsBanner(): OrphanBannerData & { refresh: () => Promise<void> } {
  const { banner, refresh } = useOrphanWells({
    autoRefresh: true,
    refreshInterval: 2 * 60 * 1000, // 2 minutes for banner freshness
  });
  
  return {
    ...banner,
    refresh,
  };
}

/**
 * Hook for quick orphan count (header badge, sidebar indicator)
 */
export function useOrphanCount(): {
  count: number;
  hasEmergency: boolean;
  hasCritical: boolean;
  loading: boolean;
  icon: string;
  color: string;
} {
  const { statistics, loading, hasEmergency, hasCritical } = useOrphanWells({
    autoRefresh: true,
    refreshInterval: 3 * 60 * 1000, // 3 minutes
  });
  
  let icon = '✅';
  let color = 'green';
  
  if (statistics.emergencyCount > 0) {
    icon = '💀';
    color = 'red';
  } else if (statistics.criticalCount > 0) {
    icon = '🔴';
    color = 'red';
  } else if (statistics.warningCount > 0) {
    icon = '🟠';
    color = 'orange';
  } else if (statistics.newCount > 0) {
    icon = '🟡';
    color = 'yellow';
  }
  
  return {
    count: statistics.total,
    hasEmergency,
    hasCritical,
    loading,
    icon,
    color,
  };
}

/**
 * Hook for orphan well list with all filtering/sorting options
 */
export function useOrphanWellsList(options?: {
  sortBy?: OrphanSortBy;
  sortDirection?: 'asc' | 'desc';
  severityFilter?: OrphanSeverity[];
  limit?: number;
}): {
  wells: OrphanWellInfo[];
  statistics: OrphanStatistics;
  loading: boolean;
  error: string | null;
  sortBy: (sortBy: OrphanSortBy, direction?: 'asc' | 'desc') => void;
  filterBySeverity: (severities: OrphanSeverity[]) => void;
  clearFilters: () => void;
  refresh: () => Promise<void>;
} {
  const {
    orphanWells,
    statistics,
    loading,
    error,
    sortBy,
    filterBySeverity,
    clearFilters,
    refresh,
  } = useOrphanWells({
    sortBy: options?.sortBy,
    sortDirection: options?.sortDirection,
    severityFilter: options?.severityFilter,
    limit: options?.limit,
    autoRefresh: true,
    refreshInterval: 5 * 60 * 1000, // 5 minutes for list view
  });
  
  return {
    wells: orphanWells,
    statistics,
    loading,
    error,
    sortBy,
    filterBySeverity,
    clearFilters,
    refresh,
  };
}

/**
 * Hook for manager dashboard with alert controls
 */
export function useOrphanWellsManager(managerIds: string[]): {
  orphanWells: OrphanWellInfo[];
  statistics: OrphanStatistics;
  banner: OrphanBannerData;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  sendAlert: () => Promise<boolean>;
  shouldSendAlert: boolean;
  getEmergencyOrphans: () => OrphanWellInfo[];
  getCriticalOrphans: () => OrphanWellInfo[];
  getTopPriorityOrphans: (count: number) => OrphanWellInfo[];
} {
  const hook = useOrphanWells({
    managerIds,
    autoRefresh: true,
    refreshInterval: 3 * 60 * 1000, // 3 minutes for manager view
    refreshOnAssignmentChange: true,
    sortBy: 'severity',
    sortDirection: 'desc',
  });
  
  return {
    orphanWells: hook.orphanWells,
    statistics: hook.statistics,
    banner: hook.banner,
    loading: hook.loading,
    error: hook.error,
    refresh: hook.refresh,
    sendAlert: hook.sendOrphanAlert,
    shouldSendAlert: hook.shouldSendAlert(),
    getEmergencyOrphans: hook.getEmergencyOrphans,
    getCriticalOrphans: hook.getCriticalOrphans,
    getTopPriorityOrphans: hook.getTopPriorityOrphans,
  };
}

/**
 * Hook for bulk assignment flow
 * Returns orphan wells ready for multi-select assignment
 */
export function useOrphanWellsForAssignment(): {
  wells: OrphanWellInfo[];
  count: number;
  loading: boolean;
  emergencyCount: number;
  criticalCount: number;
  warningCount: number;
  newCount: number;
  totalLostRevenue: number;
  refresh: () => Promise<void>;
  getByIds: (ids: string[]) => OrphanWellInfo[];
} {
  const {
    orphanWells,
    statistics,
    loading,
    refresh,
    getOrphansByIds,
  } = useOrphanWells({
    sortBy: 'severity',
    sortDirection: 'desc',
    autoRefresh: true,
    refreshOnAssignmentChange: true,
  });
  
  return {
    wells: orphanWells,
    count: statistics.total,
    loading,
    emergencyCount: statistics.emergencyCount,
    criticalCount: statistics.criticalCount,
    warningCount: statistics.warningCount,
    newCount: statistics.newCount,
    totalLostRevenue: statistics.totalLostRevenue,
    refresh,
    getByIds: getOrphansByIds,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

export {
  calculateSeverity,
  getSeverityScore,
  calculateProductionRisk,
  SEVERITY_THRESHOLDS,
  SEVERITY_COLORS,
  SEVERITY_ICONS,
  OIL_PRICE_PER_BBL,
  isOrphanWell,
};

